import React, { useEffect, useRef, useState } from "react";
import PageHeader from "@/components/PageHeader";
import TButton from "@/components/TButton";
import { Send, Sparkles, Loader2, Bot, User as UserIcon, Brain } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { API_BASE } from "@/lib/api";

const STARTERS = {
  soc: [
    "Triage the latest critical vulnerabilities and propose containment.",
    "Explain T1059.001 in our context and recommend hunting queries.",
    "What MITRE techniques are most relevant given our exposed assets?",
    "Draft an incident handover note for the on-call analyst.",
  ],
  ciso: [
    "Summarize our current cyber risk posture for the board.",
    "What is the financial impact of our top 3 exposures?",
    "Compare our compliance posture against industry peers.",
    "Recommend the top 3 strategic security investments for next quarter.",
  ],
};

const PERSONAS = {
  soc: { label: "SOC Copilot", color: "cyan", icon: Bot, title: "SOC Copilot", tag: "Defence & Monitor" },
  ciso: { label: "CISO Advisor AI", color: "fuchsia", icon: Brain, title: "CISO Advisor AI", tag: "Governance / Executive" },
};

export default function AIChat({ persona = "soc" }) {
  const cfg = PERSONAS[persona];
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs]);

  // reset when persona changes
  useEffect(() => { setMsgs([]); }, [persona]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    setMsgs((p) => [
      ...p,
      { id: `u-${Date.now()}`, role: "user", content },
      { id: `a-${Date.now()}`, role: "assistant", content: "" },
    ]);
    setBusy(true);

    try {
      // Read CSRF cookie (non-HttpOnly) for state-changing request
      const csrf = (document.cookie.match(/(?:^|; )csx_csrf=([^;]*)/) || [])[1];
      const headers = { "Content-Type": "application/json" };
      if (csrf) headers["X-CSRF-Token"] = decodeURIComponent(csrf);
      const res = await fetch(`${API_BASE}/copilot/chat`, {
        method: "POST",
        credentials: "include", // send httpOnly auth cookie
        headers,
        body: JSON.stringify({ session_id: sessionId, message: content, persona }),
      });
      if (!res.ok || !res.body) throw new Error("stream error");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          try {
            const j = JSON.parse(p.replace(/^data:\s*/, ""));
            if (j.delta) {
              setMsgs((prev) => {
                const arr = [...prev];
                const last = arr[arr.length - 1];
                arr[arr.length - 1] = { ...last, content: last.content + j.delta };
                return arr;
              });
            }
          } catch (err) {
            console.error("[chat] SSE parse error:", err);
          }
        }
      }
    } catch (e) {
      console.error("[chat] stream failed:", e);
      setMsgs((prev) => {
        const arr = [...prev];
        const last = arr[arr.length - 1];
        arr[arr.length - 1] = { ...last, content: "[!] Stream error. Please retry." };
        return arr;
      });
    } finally {
      setBusy(false);
    }
  };

  const accentBorder = cfg.color === "cyan" ? "border-cyan-500/40" : "border-fuchsia-500/40";
  const accentText = cfg.color === "cyan" ? "text-cyan-300 text-glow-cyan" : "text-fuchsia-300 text-glow-pink";

  return (
    <div className="p-6 max-w-6xl">
      <PageHeader
        tag={cfg.tag}
        title={cfg.title}
        accent={cfg.color === "fuchsia" ? "pink" : "cyan"}
        subtitle={
          persona === "soc"
            ? "Senior SOC analyst AI — triage alerts, suggest containment, write hunt queries."
            : "Translates technical findings into board-ready risk narrative and strategic actions."
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 csx-card p-0 flex flex-col h-[calc(100vh-14rem)]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
            {msgs.length === 0 && (
              <div className="text-center text-slate-500 text-sm py-12">
                <cfg.icon className={`h-10 w-10 mx-auto mb-3 ${accentText}`} />
                <div className="uppercase tracking-[0.3em] text-[10px]">Awaiting Operator Input</div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={m.id} className={["flex gap-3", m.role === "user" ? "justify-end" : ""].join(" ")}>
                {m.role === "assistant" && (
                  <div className={`shrink-0 w-8 h-8 border ${accentBorder} flex items-center justify-center`}>
                    <cfg.icon className={`h-4 w-4 ${accentText}`} />
                  </div>
                )}
                <div className={[
                  "max-w-[78%] p-3 text-sm border whitespace-pre-wrap leading-relaxed",
                  m.role === "user"
                    ? "bg-slate-900 border-slate-700 text-slate-100"
                    : `bg-slate-950/60 ${accentBorder} text-slate-200`
                ].join(" ")}>
                  {m.content || (busy && i === msgs.length - 1 ? <span className="text-slate-500">▌</span> : "")}
                </div>
                {m.role === "user" && (
                  <div className="shrink-0 w-8 h-8 border border-slate-700 flex items-center justify-center">
                    <UserIcon className="h-4 w-4 text-slate-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="border-t border-slate-800 p-3 flex items-center gap-2">
            <input
              data-testid="chat-input"
              className="flex-1 bg-slate-950 border border-slate-800 px-3 py-2.5 text-sm font-mono outline-none focus:border-cyan-500/70"
              placeholder={`Message ${cfg.title}...`}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              disabled={busy}
            />
            <TButton data-testid="chat-send" icon={busy ? Loader2 : Send} onClick={() => send()} disabled={busy || !input.trim()}
              variant={persona === "ciso" ? "accent" : "primary"}>
              {busy ? "Streaming" : "Send"}
            </TButton>
          </div>
        </div>

        <div className="csx-card p-5">
          <div className={`flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] ${accentText} mb-3`}>
            <Sparkles className="h-3 w-3" /> Quick Prompts
          </div>
          <div className="space-y-2">
            {STARTERS[persona].map((s, i) => (
              <button
                key={s}
                data-testid={`starter-${i}`}
                onClick={() => send(s)}
                disabled={busy}
                className="w-full text-left text-[12px] border border-slate-800 hover:border-cyan-500/60 hover:text-cyan-300 p-2.5 text-slate-400 transition-all"
              >
                {s}
              </button>
            ))}
          </div>
          <div className="mt-5 pt-5 border-t border-slate-800 text-[10px] text-slate-500">
            <div className="uppercase tracking-widest mb-1">Operator</div>
            <div className="text-slate-300">{user?.name}</div>
            <div className="text-cyan-400 uppercase tracking-widest">{user?.role}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
